/**
 * SmartSelect - Dropdown Unificado com Auto-Detecção de Estratégia
 *
 * NOVA IMPLEMENTAÇÃO: Campo de busca integrado (não separado no dropdown)
 *
 * Substitui SearchableSelect e AsyncSearchableSelect com uma API simplificada.
 * Decide automaticamente quando usar busca client-side ou server-side baseado
 * na configuração central em dropdownConfig.js.
 *
 * === MODOS DE USO ===
 *
 * 1. Entity-based (RECOMENDADO - mais simples):
 *    <SmartSelect entity="cidade" value={cidade} onChange={setCidade} />
 *
 * 2. Manual options (compatibilidade com SearchableSelect):
 *    <SmartSelect options={options} value={value} onChange={setValue} label="cidade" />
 *
 * 3. Manual onSearch (compatibilidade com AsyncSearchableSelect):
 *    <SmartSelect onSearch={searchFn} value={value} onChange={setValue} label="produto" />
 *
 * === PLACEHOLDER AUTOMÁTICO ===
 *
 * A prop `label` gera o placeholder automaticamente conforme a strategy:
 *   - server (onSearch): "Buscar {label}..."
 *   - client (options):  "Selecionar {label}..."
 * A prop `placeholder` explícita sempre tem prioridade sobre `label`.
 *
 * @see dropdownConfig.js - Configuração de entidades
 * @see genericServerSearch.js - Busca server-side
 * @see useEntityRowCount.js - Cache de contagem
 */

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useImperativeHandle,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ChangeEvent,
  type Ref,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Plus, Pencil, ChevronDown, X, Loader2 } from 'lucide-react';
import {
  getEntityConfig,
  isKnownLargeEntity,
  ROW_COUNT_THRESHOLD,
  type SearchStrategy
} from '@/config/dropdownConfig';
import {
  genericServerSearch,
  loadAllEntityOptions,
  type DropdownOption
} from '@/utils/genericServerSearch';
import { useEntityRowCount } from '@/hooks/useEntityRowCount';
import { normalizeForSearch, parseSearchTerms } from '@/utils/multiTermSearch';
import '@/components/ui/SmartSelect/SmartSelect.css';

// Constante estável para evitar re-renders (objeto vazio como default de props)
const EMPTY_OBJECT: Record<string, never> = {};

// =============================================================================
// TYPES
// =============================================================================

/** Handle exposed via `selectRef` (imperative focus/open/close) */
export interface SmartSelectHandle {
  focus: () => void;
  open: () => void;
  close: () => void;
}

/**
 * Value stored by SmartSelect — may be:
 *   - full option object (preferred, carries value + label)
 *   - raw id string (UUID) — resolved via loaded options
 *   - partial { value } object — resolved via loaded options
 *   - null when cleared
 */
export type SmartSelectValue = DropdownOption | { value: unknown; label?: string } | string | null;

/** Option list item — compatible with DropdownOption */
export type SmartSelectOption = DropdownOption;

/** Server-search function signature (manual mode) */
export type SmartSelectSearchFn = (
  query: string,
  filters: Record<string, unknown>
) => Promise<DropdownOption[]>;

export interface SmartSelectProps {
  // --- Entity-based ---
  entity?: string;
  entityOptions?: Record<string, unknown>;

  // --- Manual (compatibilidade) ---
  options?: SmartSelectOption[];
  onSearch?: SmartSelectSearchFn;

  // --- Comuns ---
  value?: SmartSelectValue;
  onChange?: (next: SmartSelectOption | null) => void;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  tabIndex?: number;
  selectRef?: Ref<SmartSelectHandle>;
  isClearable?: boolean;

  // --- Client-side specific ---
  onAddNew?: (searchText: string) => void;
  addNewLabel?: string;
  showAddNewOnlyWhenEmpty?: boolean;
  onEditItem?: (item: SmartSelectOption) => void;
  formatOptionLabel?: (option: SmartSelectOption) => ReactNode;
  /** Returns the string rendered inside the input; keep plain text (no JSX). */
  formatValue?: (value: SmartSelectOption) => string;
  autoOpen?: boolean;
  initialQuery?: string;
  onSelect?: () => void;

  // --- Server-side specific ---
  debounceMs?: number;
  minChars?: number;
  renderOption?: (option: SmartSelectOption) => ReactNode;
  /** Alias for formatValue; must also return a plain string for the input. */
  renderValue?: (value: SmartSelectOption) => string;
  /** Filtros de contexto passados para onSearch(query, filters) */
  searchFilters?: Record<string, unknown>;
  /** Texto para indicar filtro ativo (ex: "Casa Silva" -> placeholder "Buscar em Casa Silva...") */
  filterHint?: string;

  // --- Override ---
  forceStrategy?: SearchStrategy;
}

/** Dropdown positioning style — `position` is also used as a render-ready flag */
type DropdownPositionStyle = CSSProperties & { position?: 'fixed' };

/**
 * SmartSelect - Dropdown unificado com auto-detecção
 */
export default function SmartSelect({
  // Entity-based
  entity,
  entityOptions = EMPTY_OBJECT,

  // Manual (compatibilidade)
  options: manualOptions,
  onSearch: manualOnSearch,

  // Comuns
  value,
  onChange,
  label,
  placeholder: placeholderProp,
  isDisabled = false,
  tabIndex,
  selectRef,
  isClearable = true,

  // Client-side specific
  onAddNew,
  addNewLabel = 'Adicionar novo...',
  showAddNewOnlyWhenEmpty = false,
  onEditItem,
  formatOptionLabel,
  formatValue,
  autoOpen = false,
  initialQuery = '',
  onSelect,

  // Server-side specific
  debounceMs = 300,
  minChars = 0,
  renderOption,
  renderValue,
  searchFilters = EMPTY_OBJECT, // Filtros de contexto passados para onSearch(query, filters)
  filterHint, // Texto para indicar filtro ativo (ex: "Casa Silva" → placeholder "Buscar em Casa Silva...")

  // Override
  forceStrategy
}: SmartSelectProps) {
  // ===========================================================================
  // ESTADOS
  // ===========================================================================

  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>(initialQuery);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [dropdownStyle, setDropdownStyle] = useState<DropdownPositionStyle>({});

  // Server-side states
  const [serverResults, setServerResults] = useState<DropdownOption[]>([]);
  const [serverLoading, setServerLoading] = useState<boolean>(false);
  const [, setServerError] = useState<unknown>(null);
  const [hasInitialResults, setHasInitialResults] = useState<boolean>(false); // Controla se já carregou resultados iniciais

  // Entity loading states
  const [loadedOptions, setLoadedOptions] = useState<DropdownOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);
  const [, setOptionsError] = useState<unknown>(null);

  // Refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===========================================================================
  // CONFIGURAÇÃO DA ENTIDADE
  // ===========================================================================

  const entityConfig = useMemo(() => {
    return entity ? getEntityConfig(entity) : null;
  }, [entity]);

  // ===========================================================================
  // CONTAGEM DE REGISTROS (para modo 'auto')
  // ===========================================================================

  const { count: rowCount, loading: countLoading } = useEntityRowCount(entity ?? '');

  // ===========================================================================
  // DETERMINAÇÃO DA ESTRATÉGIA
  // ===========================================================================

  const strategy = useMemo<SearchStrategy>(() => {
    if (forceStrategy) return forceStrategy;
    if (manualOnSearch) return 'server';
    if (manualOptions) return 'client';

    if (entityConfig) {
      if (entityConfig.strategy === 'server') return 'server';
      if (entityConfig.strategy === 'client') return 'client';

      if (entityConfig.strategy === 'auto') {
        if (countLoading) {
          if (entity && isKnownLargeEntity(entity)) return 'server';
          return 'client';
        }
        if (rowCount !== null) {
          const threshold = entityConfig.threshold || ROW_COUNT_THRESHOLD;
          return rowCount > threshold ? 'server' : 'client';
        }
      }
    }

    return 'client';
  }, [forceStrategy, manualOnSearch, manualOptions, entityConfig, rowCount, countLoading, entity]);

  // ===========================================================================
  // EXPOR MÉTODOS VIA REF
  // ===========================================================================

  useImperativeHandle(selectRef, () => ({
    focus: () => {
      inputRef.current?.focus();
      if (!isDisabled) {
        setTimeout(() => setOpen(true), 50);
      }
    },
    open: () => setOpen(true),
    close: () => setOpen(false)
  }));

  // ===========================================================================
  // CARREGAR OPÇÕES (modo client-side com entity)
  // ===========================================================================

  useEffect(() => {
    if (!entity || !entityConfig || strategy !== 'client' || manualOptions) {
      return;
    }

    const loadOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(null);

      try {
        const options = await loadAllEntityOptions(entity, entityOptions);
        setLoadedOptions(options);
      } catch (err) {
        console.error(`[SmartSelect] Erro ao carregar opções de ${entity}:`, err);
        setOptionsError(err);
      } finally {
        setOptionsLoading(false);
      }
    };

    loadOptions();
  }, [entity, entityConfig, strategy, manualOptions, entityOptions]);

  // ===========================================================================
  // BUSCA SERVER-SIDE COM DEBOUNCE
  // ===========================================================================

  const performServerSearch = useCallback(async (searchQuery: string) => {
    if (strategy !== 'server') return;

    // Verifica mínimo de caracteres
    if (searchQuery.length < minChars) {
      setServerResults([]);
      return;
    }

    setServerLoading(true);
    setServerError(null);

    try {
      let results: DropdownOption[];
      if (manualOnSearch) {
        // Passa searchFilters como segundo argumento para permitir filtragem contextual
        results = await manualOnSearch(searchQuery, searchFilters);
      } else if (entity && entityConfig) {
        results = await genericServerSearch(entity, searchQuery, entityOptions);
      } else {
        results = [];
      }
      setServerResults(results || []);
      setHasInitialResults(true); // Marca que já carregou resultados
    } catch (err) {
      console.error('[SmartSelect] Erro na busca:', err);
      setServerError(err);
      setServerResults([]);
    } finally {
      setServerLoading(false);
    }
  }, [strategy, minChars, manualOnSearch, entity, entityConfig, entityOptions, searchFilters]);

  // Debounced search for server mode
  useEffect(() => {
    if (strategy !== 'server' || !open) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performServerSearch(query);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, strategy, open, debounceMs, performServerSearch]);

  // Initial server search when opening
  useEffect(() => {
    if (strategy === 'server' && open && serverResults.length === 0 && !serverLoading) {
      performServerSearch(query);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, strategy]);

  // ===========================================================================
  // OPÇÕES FILTRADAS (modo client)
  // ===========================================================================

  const clientOptions = useMemo<DropdownOption[]>(() => {
    if (manualOptions) return manualOptions;
    return loadedOptions;
  }, [manualOptions, loadedOptions]);

  // Normalizar value: se for string (UUID), resolver para objeto {value, label}
  const resolvedValue = useMemo<DropdownOption | { value: unknown; label?: string } | null>(() => {
    if (!value) return null;
    // Se já é objeto com label, usar como está
    if (typeof value === 'object' && 'label' in value && value.label) return value;
    // Se é string (UUID) ou objeto sem label, tentar resolver via opções carregadas
    const rawId = typeof value === 'string' ? value : value.value;
    if (!rawId) return null;
    const found = clientOptions.find((o) => String(o.value) === String(rawId));
    if (found) return found;
    // Fallback: retornar objeto com value sem label (display ficará vazio até opções carregarem)
    return typeof value === 'string' ? { value, label: '' } : value;
  }, [value, clientOptions]);

  const filteredOptions = useMemo<DropdownOption[]>(() => {
    if (strategy === 'server') {
      return serverResults;
    }

    const searchTerms = parseSearchTerms(query);

    // Sem busca: retorna todas as opções
    if (searchTerms.length === 0) {
      return clientOptions;
    }

    // Com busca: filtra as opções
    return clientOptions.filter((o) => {
      const searchTarget = o.searchText
        ? normalizeForSearch(o.searchText)
        : normalizeForSearch(o.label || '');
      return searchTerms.every((term) => searchTarget.includes(term));
    });
  }, [strategy, serverResults, clientOptions, query]);

  // ===========================================================================
  // HANDLERS
  // ===========================================================================

  const handleOpen = useCallback(() => {
    if (isDisabled) return;
    setOpen(true);
    setQuery('');
    setHighlightedIndex(0);
  }, [isDisabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
    setDropdownStyle({});
  }, []);

  const selectItem = useCallback((opt: DropdownOption) => {
    onChange?.(opt);
    handleClose();
    if (onSelect) {
      setTimeout(() => onSelect(), 50);
    }
  }, [onChange, handleClose, onSelect]);

  const handleAddNew = useCallback(() => {
    const searchText = query.trim();
    handleClose();
    if (onAddNew) {
      onAddNew(searchText);
    }
  }, [query, handleClose, onAddNew]);

  const handleEditItem = useCallback((e: ReactMouseEvent<HTMLButtonElement>, item: DropdownOption) => {
    e.stopPropagation();
    handleClose();
    if (onEditItem) {
      onEditItem(item);
    }
  }, [handleClose, onEditItem]);

  const handleClear = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange?.(null);
  }, [onChange]);

  // ===========================================================================
  // KEYBOARD NAVIGATION
  // ===========================================================================

  const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLInputElement>) => {
    const maxVisible = 200;
    const visibleCount = Math.min(filteredOptions.length, maxVisible);
    const showAddButton = onAddNew && (!showAddNewOnlyWhenEmpty || query.trim() || filteredOptions.length === 0);
    const totalItems = visibleCount + (showAddButton ? 1 : 0);
    const addButtonIndex = visibleCount;

    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleOpen();
        setQuery(e.key);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex === addButtonIndex && showAddButton) {
            handleAddNew();
          } else if (filteredOptions[highlightedIndex]) {
            selectItem(filteredOptions[highlightedIndex]);
          }
        }
        break;
      case 'Tab':
        if (filteredOptions.length === 1) {
          e.preventDefault();
          selectItem(filteredOptions[0]);
        } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          e.preventDefault();
          selectItem(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  }, [open, filteredOptions, highlightedIndex, onAddNew, showAddNewOnlyWhenEmpty, query, handleOpen, handleAddNew, selectItem, handleClose]);

  // ===========================================================================
  // CLICK OUTSIDE
  // ===========================================================================

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      // DOM boundary: EventTarget has no .contains; narrow via Node (runtime guaranteed for mousedown targets).
      const target = e.target as unknown as Node | null;
      const isOnPanel = target ? panelRef.current?.contains(target) : false;
      const isOnContainer = target ? containerRef.current?.contains(target) : false;
      const isOnAnyPortal = target instanceof Element ? target.closest('.smart-select-dropdown') : null;

      if (!isOnPanel && !isOnContainer && !isOnAnyPortal) {
        handleClose();
      }
    };

    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open, handleClose]);

  // ===========================================================================
  // DROPDOWN POSITIONING
  // ===========================================================================

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320;
      const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      const newStyle: DropdownPositionStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 'var(--z-select-dropdown)',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '8px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)'
      };

      if (openUpward) {
        newStyle.bottom = `${window.innerHeight - rect.top + 6}px`;
      } else {
        newStyle.top = `${rect.bottom + 6}px`;
      }

      setDropdownStyle(newStyle);
    }
  }, [open]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 320;
        const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

        setDropdownStyle((prev) => ({
          ...prev,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          ...(openUpward
            ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: undefined }
            : { top: `${rect.bottom + 6}px`, bottom: undefined }
          )
        }));
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  // AutoOpen
  useEffect(() => {
    if (autoOpen && !isDisabled) {
      const timer = setTimeout(() => handleOpen(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, isDisabled, handleOpen]);

  // Reset highlighted when options change
  useEffect(() => {
    if (open) {
      setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
    }
  }, [open, filteredOptions.length]);

  // Auto-scroll para manter item destacado visível ao navegar com setas
  useEffect(() => {
    if (!open || highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    const item = items[highlightedIndex];
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlightedIndex]);

  // ===========================================================================
  // RENDER HELPERS
  // ===========================================================================

  const displayValue = useMemo<string>(() => {
    if (!resolvedValue) return '';
    // resolvedValue may be a full DropdownOption or a partial {value,label} fallback.
    // formatValue/renderValue are typed to return strings (input expects a string).
    // Two-step narrow: callers of formatValue/renderValue own the contract that the
    // value carries the same extra fields they serialize at the call site.
    const asOption = resolvedValue as unknown as DropdownOption;
    if (formatValue) return formatValue(asOption);
    if (renderValue) return renderValue(asOption);
    return resolvedValue.label || '';
  }, [resolvedValue, formatValue, renderValue]);

  // Placeholder inteligente: resolve baseado em strategy + label
  // Prioridade: placeholderProp explícito > label + strategy > genérico
  const effectivePlaceholder = useMemo<string>(() => {
    let base: string;
    if (placeholderProp) {
      base = placeholderProp;
    } else if (label) {
      base = strategy === 'server'
        ? `Buscar ${label}...`
        : `Selecionar ${label}...`;
    } else {
      base = strategy === 'server' ? 'Buscar...' : 'Selecionar...';
    }

    // Quando há filterHint ativo, adiciona contexto (ex: "Buscar ponto em Casa Silva...")
    const contextValues = { ...searchFilters, ...entityOptions };
    const hasContextFilter = Object.values(contextValues).some(
      (v) => v != null && v !== ''
    );
    if (filterHint && hasContextFilter) {
      const withoutEllipsis = base.replace(/\.{3}$/, '').trim();
      return `${withoutEllipsis} em ${filterHint}...`;
    }

    return base;
  }, [placeholderProp, label, strategy, filterHint, searchFilters, entityOptions]);

  const renderOptionFn = formatOptionLabel || renderOption;

  // Só mostra loading se ainda não tem resultados iniciais carregados
  // Se já tem resultados, a busca acontece silenciosamente (UX mais suave)
  const isLoading = optionsLoading || (serverLoading && !hasInitialResults);

  // ===========================================================================
  // DROPDOWN CONTENT
  // ===========================================================================

  const dropdownContent = open && dropdownStyle.position && (
    <div
      ref={panelRef}
      className="smart-select-dropdown"
      style={dropdownStyle}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Lista de resultados */}
      <div ref={listRef} className="smart-select-list" role="listbox">
        {/* Loading state */}
        {isLoading && (
          <div className="smart-select-empty" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Loader2 size={16} className="smart-select-spinner" />
            Carregando...
          </div>
        )}

        {/* Nada encontrado */}
        {!isLoading && filteredOptions.length === 0 && !onAddNew && (
          <div className="smart-select-empty">Nada encontrado</div>
        )}

        {/* Server mode: hint to type more */}
        {strategy === 'server' && !isLoading && filteredOptions.length === 0 && query.length < minChars && minChars > 0 && (
          <div className="smart-select-empty" style={{ fontSize: '12px' }}>
            Digite pelo menos {minChars} caractere{minChars > 1 ? 's' : ''} para buscar...
          </div>
        )}

        {/* Aviso de muitos resultados */}
        {!isLoading && filteredOptions.length > 200 && !query.trim() && (
          <div className="smart-select-empty" style={{ fontSize: '12px' }}>
            {filteredOptions.length} resultados. Digite para filtrar...
          </div>
        )}

        {/* Lista de opções */}
        {!isLoading && filteredOptions.slice(0, 200).map((o, index) => {
          const selected = resolvedValue && String(resolvedValue.value) === String(o.value);
          const isHighlighted = index === highlightedIndex;

          return (
            <div
              key={String(o.value)}
              role="option"
              aria-selected={selected ?? undefined}
              className={`smart-select-item${isHighlighted ? ' smart-select-item-highlighted' : ''}`}
              onClick={() => selectItem(o)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {renderOptionFn ? renderOptionFn(o) : o.label}
              </span>
              {onEditItem && isHighlighted && (
                <button
                  type="button"
                  className="smart-select-edit"
                  onClick={(e) => handleEditItem(e, o)}
                  title="Editar"
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Botão adicionar novo */}
      {onAddNew && (!showAddNewOnlyWhenEmpty || query.trim() || filteredOptions.length === 0) && (() => {
        const visibleCount = Math.min(filteredOptions.length, 200);
        const isAddButtonHighlighted = highlightedIndex === visibleCount;
        return (
          <div
            className={`smart-select-add${isAddButtonHighlighted ? ' smart-select-add-highlighted' : ''}`}
            onClick={handleAddNew}
            onMouseEnter={() => setHighlightedIndex(visibleCount)}
            style={{ borderTop: filteredOptions.length > 0 ? '1px solid #e5e7eb' : 'none' }}
          >
            <Plus size={14} />
            {query.trim() ? `${addNewLabel} "${query.trim()}"` : addNewLabel}
          </div>
        );
      })()}
    </div>
  );

  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================

  return (
    <div className="smart-select" ref={containerRef}>
      <div
        className={`smart-select-field${isDisabled ? ' smart-select-field-disabled' : ''}`}
        onClick={() => !isDisabled && inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          type="text"
          className="smart-select-input"
          value={open ? query : displayValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (!open) handleOpen();
            setQuery(e.target.value);
            setHighlightedIndex(0);
          }}
          onFocus={() => !open && handleOpen()}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={isDisabled}
          tabIndex={tabIndex}
          autoComplete="off"
        />

        {/* Clear button */}
        {isClearable && value && !isDisabled && (
          <button
            type="button"
            className="smart-select-clear"
            onClick={handleClear}
            title="Limpar"
          >
            <X size={16} />
          </button>
        )}

        {/* Dropdown arrow / loading indicator */}
        <span className={`smart-select-chevron${open ? ' smart-select-chevron-open' : ''}`}>
          {isLoading ? (
            <Loader2 size={16} className="smart-select-spinner" />
          ) : (
            <ChevronDown size={16} />
          )}
        </span>
      </div>

      {/* Portal dropdown */}
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}

// =============================================================================
// COMPONENTE DE CONVENIÊNCIA PARA ENTITY
// =============================================================================

/**
 * EntitySelect - Versão simplificada do SmartSelect para uso com entity
 *
 * @example
 * <EntitySelect entity="cidade" value={cidade} onChange={setCidade} />
 */
export function EntitySelect({ entity, ...props }: SmartSelectProps & { entity: string }) {
  return <SmartSelect entity={entity} {...props} />;
}
